import { useState, useRef } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Download, Sparkles, Wand2, Plus, X, ImageIcon, Save } from 'lucide-react';
import { toast } from 'sonner';

const STYLES = ['Photorealistic', 'Digital Art', 'Oil Painting', 'Watercolor', 'Pencil Sketch', 'Anime', '3D Render', 'Pop Art'];

export default function ImageGeneratorPage() {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('');
  const [loading, setLoading] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [history, setHistory] = useState([]);

  // Upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearUpload = () => {
    setUploadedFile(null);
    setUploadPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const generate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setImageData(null);
    try {
      let res;
      if (uploadedFile) {
        // Image edit mode — upload + prompt
        const fd = new FormData();
        fd.append('file', uploadedFile);
        fd.append('prompt', style ? `${prompt.trim()}, ${style.toLowerCase()} style` : prompt.trim());
        res = await api.post('/generate/image/edit', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
      } else {
        // Text-to-image mode
        const fullPrompt = style ? `${prompt.trim()}, ${style.toLowerCase()} style` : prompt.trim();
        res = await api.post('/generate/image', { prompt: fullPrompt }, { timeout: 120000 });
      }
      setImageData(res.data.image_base64);
      setHistory(prev => [{ prompt: prompt.trim(), image: res.data.image_base64, created: new Date().toISOString(), hadUpload: !!uploadedFile }, ...prev].slice(0, 12));
      toast.success(uploadedFile ? 'Image created from your upload' : 'Image generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = (b64) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${b64 || imageData}`;
    link.download = `ai-image-${Date.now()}.png`;
    link.click();
  };

  return (
    <div className="max-w-3xl mx-auto animate-in" data-testid="image-generator-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-gold/10 flex items-center justify-center">
          <Sparkles size={18} className="text-gold" />
        </div>
        <div>
          <h1 className="font-serif text-2xl md:text-3xl">AI Image Creator</h1>
          <p className="text-sm text-muted-foreground">Create from text or transform an uploaded image</p>
        </div>
      </div>

      {/* Upload Zone */}
      <div className="border border-border/40 p-5 mt-6 mb-0 rounded-t-xl" data-testid="image-upload-zone">
        <div className="flex items-center justify-between mb-3">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Source Image (optional)</label>
          {uploadPreview && (
            <button onClick={clearUpload} className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-1">
              <X size={10} /> Remove
            </button>
          )}
        </div>

        {uploadPreview ? (
          <div className="flex items-start gap-4">
            <img src={uploadPreview} alt="Upload" className="w-24 h-24 rounded-xl object-cover border border-border/30 shadow-sm" data-testid="upload-preview" />
            <div className="flex-1 pt-1">
              <p className="text-xs font-medium text-obsidian">{uploadedFile?.name}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{(uploadedFile?.size / 1024).toFixed(0)} KB</p>
              <p className="text-[10px] text-violet-600 mt-2 font-medium">AI will create something new using this image</p>
            </div>
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-violet-200 rounded-xl py-6 flex flex-col items-center gap-2 hover:border-violet-400 hover:bg-violet-50/30 transition-all cursor-pointer"
            data-testid="upload-btn"
          >
            <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
              <Plus size={20} className="text-violet-500" />
            </div>
            <p className="text-xs font-medium text-obsidian">Upload an image to transform</p>
            <p className="text-[10px] text-muted-foreground">or skip to create from text only</p>
          </button>
        )}
        <input ref={fileRef} type="file" className="hidden" accept="image/*" onChange={handleUpload} />
      </div>

      {/* Prompt Area */}
      <div className="border border-t-0 border-border/40 p-5 rounded-b-xl mb-6">
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-2">
          {uploadedFile ? 'What should AI create with this image?' : 'Describe your image'}
        </label>
        <Textarea
          data-testid="image-gen-prompt"
          placeholder={uploadedFile
            ? 'e.g. Create a luxury bedroom scene with this wallpaper on the wall...'
            : 'A golden sunset over a Japanese temple with cherry blossoms...'
          }
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          className="rounded-lg border-border/40 bg-transparent px-3 py-2 shadow-none focus-visible:ring-1 focus-visible:ring-violet-300 resize-none text-sm min-h-[70px]"
          rows={3}
        />

        {/* Style Picker */}
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mt-4 mb-2">Style (optional)</label>
        <div className="flex flex-wrap gap-2" data-testid="style-picker">
          {STYLES.map(s => (
            <button
              key={s}
              onClick={() => setStyle(style === s ? '' : s)}
              className={`px-3 py-1.5 text-[10px] rounded-full border transition-all duration-200 ${
                style === s
                  ? 'bg-obsidian text-white border-obsidian'
                  : 'border-border/40 hover:border-violet-300 text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`style-${s.toLowerCase().replace(' ', '-')}`}
            >
              {s}
            </button>
          ))}
        </div>

        <Button
          data-testid="generate-image-btn"
          onClick={generate}
          disabled={!prompt.trim() || loading}
          className="w-full mt-5 rounded-xl h-12 text-xs uppercase tracking-wider bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white transition-all duration-300 disabled:opacity-40"
        >
          {loading ? (
            <><Loader2 size={14} className="mr-2 animate-spin" /> {uploadedFile ? 'Creating from your image...' : 'Generating (up to 60s)...'}</>
          ) : (
            <><Wand2 size={14} className="mr-2" /> {uploadedFile ? 'Create with Image' : 'Generate Image'}</>
          )}
        </Button>
      </div>

      {/* Result */}
      {imageData && (
        <div className="border border-border/40 rounded-xl overflow-hidden mb-8 animate-in shadow-lg" data-testid="generated-image-result">
          <img src={`data:image/png;base64,${imageData}`} alt="AI Generated" className="w-full" data-testid="generated-image" />
          <div className="p-4 flex items-center justify-between bg-stone-50">
            <p className="text-xs text-muted-foreground truncate max-w-[60%]">{prompt}</p>
            <div className="flex gap-2">
              <Button data-testid="download-image-btn" variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => downloadImage()}>
                <Download size={12} className="mr-1" /> Download
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      {history.length > 1 && (
        <div data-testid="generation-history">
          <h2 className="font-serif text-lg mb-4">Recent Creations</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {history.slice(1).map((h, i) => (
              <div key={i} className="border border-border/30 rounded-xl overflow-hidden group cursor-pointer hover:border-violet-300 hover:shadow-md transition-all" data-testid={`history-item-${i}`}>
                <div className="relative">
                  <img src={`data:image/png;base64,${h.image}`} alt="" className="w-full aspect-square object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <button onClick={() => downloadImage(h.image)} className="p-2 bg-white/90 rounded-full">
                      <Download size={14} />
                    </button>
                  </div>
                  {h.hadUpload && (
                    <div className="absolute top-1.5 left-1.5 bg-violet-500 text-white text-[7px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full">
                      From Upload
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground p-2 truncate">{h.prompt}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
